import { logger } from "@/lib/logger";
/**
 * Offline Form Sync Utility
 * Handles syncing of eCRF form submissions from IndexedDB to Supabase
 * 
 * CRASH-SAFE: All operations wrapped in try-catch
 */

import { supabase } from '@/integrations/supabase/client';
import { offlineDataStore } from '@/utils/offlineDataStore';
import { saveEcrfSubmission } from '@/lib/ecrf/submissionWrite';
import { toast } from 'sonner';
import { safeIsOnline } from '@/lib/browserSafeUtils';

const PENDING_SUBMISSIONS_PREFIX = 'form_submission:pending:';
const SYNC_LOCK_KEY = 'form_sync_in_progress';

export interface OfflineFormSubmission {
  id: string;
  form_id: string;
  patient_id: string;
  visit_id?: string;
  submission_data: Record<string, any>;
  status: 'draft' | 'submitted' | 'verified' | 'queried';
  submitted_by: string;
  submitted_at: string;
  offline_mode: boolean;
  created_at: string;
}

export interface SyncResult {
  synced: number;
  failed: number;
  pending: number;
  errors: string[];
}

/**
 * Get all pending offline form submissions
 */
export async function getPendingSubmissions(): Promise<OfflineFormSubmission[]> {
  try {
    const allKeys = await offlineDataStore.getAllKeys();
    const pendingKeys = allKeys.filter(key => key.startsWith(PENDING_SUBMISSIONS_PREFIX));
    
    const submissions: OfflineFormSubmission[] = [];
    for (const key of pendingKeys) {
      const data = await offlineDataStore.get<OfflineFormSubmission>(key);
      if (data) {
        submissions.push(data);
      }
    }
    
    return submissions.sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  } catch (error) {
    logger.error('[offlineFormSync] Error getting pending submissions:', error);
    return [];
  }
}

/**
 * Save a form submission offline
 */
export async function saveFormSubmissionOffline(
  submission: Omit<OfflineFormSubmission, 'id' | 'created_at' | 'offline_mode'>
): Promise<string> {
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const fullSubmission: OfflineFormSubmission = {
    ...submission,
    id,
    offline_mode: true,
    created_at: new Date().toISOString(),
  };
  
  const key = `${PENDING_SUBMISSIONS_PREFIX}${id}`;
  await offlineDataStore.set(key, fullSubmission);
  
  logger.debug('[offlineFormSync] Saved submission offline:', id);
  return id;
}

/**
 * Sync a single form submission to Supabase
 */
async function syncSingleSubmission(submission: OfflineFormSubmission): Promise<boolean> {
  try {
    // Check if already synced (idempotency)
    const { data: existing } = await supabase
      .from('patient_form_submissions')
      .select('id')
      .eq('id', submission.id)
      .maybeSingle();
    
    if (existing) {
      logger.debug('[offlineFormSync] Submission already exists, skipping:', submission.id);
      return true;
    }
    
    // Insert new submission. Trial-site staff go straight through RLS; linked
    // PHC CHWs fall back to the server-checked phc_save_ecrf_submission RPC.
    try {
      await saveEcrfSubmission({
        patient_id: submission.patient_id,
        visit_id: submission.visit_id || null,
        form_id: submission.form_id,
        submission_data: submission.submission_data as Record<string, unknown>,
        submitted_by: submission.submitted_by,
        status: 'submitted',
      });
    } catch (error) {
      logger.error('[offlineFormSync] Failed to sync submission:', submission.id, error);
      return false;
    }
    
    logger.debug('[offlineFormSync] Successfully synced submission:', submission.id);
    return true;
  } catch (error) {
    logger.error('[offlineFormSync] Exception syncing submission:', submission.id, error);
    return false;
  }
}

/**
 * Sync all pending offline form submissions to Supabase
 */
export async function syncOfflineFormSubmissions(
  showToast = true
): Promise<SyncResult> {
  const result: SyncResult = {
    synced: 0,
    failed: 0,
    pending: 0,
    errors: [],
  };
  
  // Check if sync is already in progress
  const isLocked = await offlineDataStore.get<boolean>(SYNC_LOCK_KEY);
  if (isLocked) {
    logger.debug('[offlineFormSync] Sync already in progress, skipping');
    return result;
  }
  
  // Check if online
  if (!safeIsOnline()) {
    logger.debug('[offlineFormSync] Offline, skipping sync');
    const pending = await getPendingSubmissions();
    result.pending = pending.length;
    return result;
  }
  
  try {
    // Set lock
    await offlineDataStore.set(SYNC_LOCK_KEY, true, 300); // 5 min TTL
    
    const pending = await getPendingSubmissions();
    logger.debug(`[offlineFormSync] Found ${pending.length} pending submissions`);
    
    if (pending.length === 0) {
      await offlineDataStore.deleteByKey(SYNC_LOCK_KEY);
      return result;
    }
    
    for (const submission of pending) {
      const success = await syncSingleSubmission(submission);
      
      if (success) {
        // Remove from pending queue
        await offlineDataStore.deleteByKey(`${PENDING_SUBMISSIONS_PREFIX}${submission.id}`);
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync submission ${submission.id}`);
      }
    }
    
    // Get remaining pending count
    const remainingPending = await getPendingSubmissions();
    result.pending = remainingPending.length;
    
    if (showToast && result.synced > 0) {
      toast.success(`Synced ${result.synced} form submission${result.synced > 1 ? 's' : ''}`, {
        description: result.failed > 0 
          ? `${result.failed} failed to sync` 
          : undefined,
      });
    }
    
    if (showToast && result.failed > 0 && result.synced === 0) {
      toast.error(`Failed to sync ${result.failed} form submission${result.failed > 1 ? 's' : ''}`);
    }
    
    logger.debug('[offlineFormSync] Sync complete:', result);
  } catch (error: any) {
    logger.error('[offlineFormSync] Sync error:', error);
    result.errors.push(error.message || 'Unknown sync error');
  } finally {
    // Release lock
    await offlineDataStore.deleteByKey(SYNC_LOCK_KEY);
  }
  
  return result;
}

/**
 * Initialize page-load sync for offline form submissions.
 * NOTE: The `online` event is handled by the unified syncOrchestrator
 * to prevent all sync mechanisms from firing simultaneously on reconnect.
 */
export function initializeAutoSync(): () => void {
  // Sync on page load if online (delayed to avoid blocking initial render)
  if (safeIsOnline()) {
    setTimeout(() => syncOfflineFormSubmissions(false), 5000);
  }
  
  return () => {
    // No listeners to clean up — orchestrator handles reconnect
  };
}

/**
 * Get sync status summary
 */
export async function getSyncStatus(): Promise<{
  pendingCount: number;
  lastSyncAttempt: number | null;
  isOnline: boolean;
}> {
  const pending = await getPendingSubmissions();
  
  return {
    pendingCount: pending.length,
    lastSyncAttempt: null, // Could be stored if needed
    isOnline: safeIsOnline(),
  };
}
